import * as React from 'react';
import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Modal, Spin } from 'antd';
import posthog, { CaptureResult } from 'posthog-js';
import { fetchG2ReviewToken, G2ReviewTokenResponse } from '../../thunks/g2_thunks';
import { NPS_SURVEY_NAME, NPS_PROMOTER_THRESHOLD } from '../../constants/G2Constants';
import { buildG2IframeUrl } from '../../utils/G2Utils';
import { withUserType } from '../withUserType';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

interface SurveyQuestion {
	question: string;
	response: string | number;
}

interface G2ReviewPromptProps {
	currentUser: { email?: string } | null;
	userType: string;
	fetchG2ReviewToken: (email: string) => Promise<G2ReviewTokenResponse>;
}

type Status = 'idle' | 'loading' | 'open';

const G2ReviewPrompt: React.FC<G2ReviewPromptProps> = (props) => {
	const { currentUser, userType, fetchG2ReviewToken } = props;
	const [status, setStatus] = useState<Status>('idle');
	const [iframeUrl, setIframeUrl] = useState('');

	const handleClose = () => setStatus('idle');

	useEffect(() => {
		const email = nullSafeGet('email', currentUser);
		if (!email) {
			return;
		}
		let cancelled = false;

		const unsubscribe = posthog.on('eventCaptured', (event: CaptureResult) => {
			if (!event || event.event !== 'survey sent') {
				return;
			}

			const properties = nullSafeGetOrElse('properties', event, {});
			if (
				properties['$survey_name'] !== NPS_SURVEY_NAME ||
				properties['$survey_completed'] !== true
			) {
				return;
			}

			const surveyQuestions: SurveyQuestion[] = nullSafeGetOrElse(
				'$survey_questions',
				properties,
				[]
			);
			const score = Number(nullSafeGet('0.response', surveyQuestions));
			if (isNaN(score) || score < NPS_PROMOTER_THRESHOLD) {
				return;
			}

			// Only proceed if the user opted in to leaving a G2 review.
			const g2OptInQuestion = surveyQuestions.find(
				(question) =>
					question &&
					typeof question.question === 'string' &&
					question.question.toLowerCase().indexOf('g2') !== -1
			);
			const optInResponse = String(nullSafeGetOrElse('response', g2OptInQuestion, ''));
			if (!/yes/i.test(optInResponse)) {
				return;
			}

			const telemetryProps = { source: 'nps_modal', user_type: userType };
			setStatus('loading');
			fetchG2ReviewToken(email)
				.then((result) => {
					if (cancelled) {
						return;
					}
					if (result.reviewExists) {
						posthog.capture('g2_review_already_exists', telemetryProps);
						setStatus('idle');
						return;
					}
					if (result.state) {
						posthog.capture('g2_review_token_issued', telemetryProps);
						setIframeUrl(buildG2IframeUrl(result.state, email));
						setStatus('open');
					} else {
						posthog.capture('g2_review_load_failed', { ...telemetryProps, reason: 'no_state' });
						setStatus('idle');
					}
				})
				.catch(() => {
					if (cancelled) {
						return;
					}
					posthog.capture('g2_review_load_failed', {
						...telemetryProps,
						reason: 'token_fetch_failed',
					});
					setStatus('idle');
				});
		});

		return () => {
			cancelled = true;
			if (unsubscribe) {
				unsubscribe();
			}
		};
	}, [currentUser, userType, fetchG2ReviewToken]);

	if (status === 'idle') {
		return null;
	}

	return (
		<Modal
			visible={true}
			title="Share your experience on G2"
			width={720}
			footer={null}
			onCancel={handleClose}
			destroyOnClose={true}
		>
			{status === 'loading' ? (
				<div className="flex items-center justify-center py-16">
					<Spin size="large" />
				</div>
			) : (
				<iframe
					src={iframeUrl}
					title="G2 Review Form"
					width="100%"
					height="550px"
					sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
					className="border-none"
					onLoad={() =>
						posthog.capture('g2_review_form_loaded', {
							source: 'nps_modal',
							user_type: userType,
						})
					}
				/>
			)}
		</Modal>
	);
};

const mapStateToProps = (state: any) => ({
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch: any, ownProps: { userType: string }) => ({
	fetchG2ReviewToken: (email: string) => dispatch(fetchG2ReviewToken(ownProps.userType, email)),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(G2ReviewPrompt));
